// 장바구니 도메인 공통 타입과 zod 스키마.
// 사용자 앱(로컬 카트), API(서버 카트), 향후 주문 생성 흐름이 모두 본 파일의 정의를 공유한다.
// 단위(g100/geun/pack/kg/each) 와 무관하게 cart_items.quantity 는 "단위 개수" 의 양의 정수로 다룬다 (Phase 3-A 결정).

import { z } from "zod";
import { PRODUCT_OPTION_MAX } from "./catalog.js";
import { type ProductUnit } from "./units.js";

// 카트 항목의 수량 한도.
// 단일 상품을 999개까지 담는 것은 도메인상 비정상이라 가정하고, 입력값을 막아 UX 사고/악의적 요청을 차단한다.
export const CART_ITEM_QUANTITY_MAX = 99;
export const CART_ITEM_QUANTITY_MIN = 1;

// 한 카트에 동시에 담을 수 있는 서로 다른 상품 수.
// 머지/추가 시 한도를 넘으면 거절한다.
export const CART_DISTINCT_ITEM_MAX = 50;

// 카트 항목 수량 스키마.
// 양의 정수, 상한 제한. 0 은 "삭제" 의미가 되지 않게 하기 위해 별도 DELETE 라우트로만 처리한다.
export const cartQuantitySchema = z
  .number()
  .int()
  .min(CART_ITEM_QUANTITY_MIN, `수량은 최소 ${CART_ITEM_QUANTITY_MIN} 이상이어야 합니다.`)
  .max(CART_ITEM_QUANTITY_MAX, `수량은 최대 ${CART_ITEM_QUANTITY_MAX} 까지 가능합니다.`);

// 카트에 새 상품을 담거나 기존 항목 수량을 증가시키는 요청.
// addQuantity 로 표현해 클라이언트는 "이만큼 더 담는다" 의미로 호출한다.
// 서버는 기존 수량 + addQuantity 가 한도를 넘으면 한도까지만 채우거나 400 으로 거절한다 (구현부에서 결정).
// 담을 때 함께 고른 추가 옵션(옵션 id + 개수). 서버가 해당 상품의 옵션인지 재검증한다.
export const cartItemOptionInputSchema = z.object({
  optionId: z.string().min(1),
  quantity: cartQuantitySchema,
});
export type CartItemOptionInput = z.infer<typeof cartItemOptionInputSchema>;

export const cartAddItemInputSchema = z.object({
  productId: z.string().min(1),
  quantity: cartQuantitySchema,
  // 추가 옵션 선택(선택 사항). 같은 상품을 다시 담으면 기본 수량과 옵션 수량이 각각 합산된다.
  options: z.array(cartItemOptionInputSchema).max(PRODUCT_OPTION_MAX).optional(),
});
export type CartAddItemInput = z.infer<typeof cartAddItemInputSchema>;

// 카트 항목 수량을 명시값으로 갱신하는 요청.
// 0 또는 음수는 받지 않고, 0 으로 만들고 싶을 때는 DELETE 라우트를 호출한다.
export const cartUpdateQuantityInputSchema = z.object({
  quantity: cartQuantitySchema,
});
export type CartUpdateQuantityInput = z.infer<typeof cartUpdateQuantityInputSchema>;

// 로그인 직후 비로그인 카트(클라이언트) 를 서버 카트로 합치는 요청.
// 동일 상품이 양쪽에 있으면 수량을 더하되, 한도(99) 를 초과하면 한도까지만 유지한다 (구현 책임).
export const cartMergeInputSchema = z.object({
  items: z
    .array(
      z.object({
        productId: z.string().min(1),
        quantity: cartQuantitySchema,
        // 게스트 카트에 담아둔 추가 옵션도 함께 옮긴다(누락 시 손님이 고른 옵션이 사라진다).
        options: z.array(cartItemOptionInputSchema).max(PRODUCT_OPTION_MAX).optional(),
      }),
    )
    .max(CART_DISTINCT_ITEM_MAX),
});
export type CartMergeInput = z.infer<typeof cartMergeInputSchema>;

// 응답: 한 항목.
// 사용자 UI 가 단위×수량 표기와 합계 계산에 필요한 최소 필드만 노출한다.
// productSnapshot 은 서버가 응답 시점에 products 의 현재 행에서 즉시 채워 보낸다.
//  - 상품이 비공개(is_visible=0) 또는 soft delete 된 경우 isAvailable=false 로 내려준다.
//  - 품절(isSoldOut=true) 도 그대로 내려 클라이언트에서 회색 처리한다.
export interface CartItemDto {
  id: string;
  productId: string;
  quantity: number;
  productSnapshot: {
    name: string;
    priceKrw: number;
    unit: ProductUnit;
    mainImageKey: string | null;
    isSoldOut: boolean;
    isAvailable: boolean;
    // 단위 기준중량(g). 팩이면 1팩 중량, 무게 단위면 단위 기준중량. 총 중량 표시에 쓴다.
    baseGramsG: number | null;
    // 실중량 관리 대상품 여부. true 면 주문 시점에 금액을 확정할 수 없어(포장 중량 변동)
    // 카드 즉시결제를 막고 현장/SMS 결제만 허용한다.
    weightAdjustable: boolean;
  };
  // 이 줄에 함께 담긴 추가 옵션. 장바구니는 상품의 현재 값을 보여주므로 옵션도 현재 마스터 값을 싣는다.
  options: CartItemOptionDto[];
  // 행 단위 합계 = 단가*수량 + 추가옵션 합계. 항목이 사용 불가(isAvailable=false) 면 0 으로 응답해
  // 총합에 잘못 더해지지 않게 한다.
  lineTotalKrw: number;
  // 이 줄의 예상 총중량(g) = 기준중량*수량 + 옵션중량 합계. 기준중량을 모르면 null.
  totalWeightG: number | null;
  createdAt: string;
  updatedAt: string;
}

// 장바구니 줄에 담긴 추가 옵션 1종.
export interface CartItemOptionDto {
  optionId: string;
  label: string;
  addGrams: number;
  addPriceKrw: number;
  quantity: number;
}

// 응답: 카트 전체.
// totalKrw 는 사용 가능 항목 합. 사용 불가/품절 항목은 별도로 셈해 클라이언트가 안내 문구를 띄울 수 있게 한다.
export interface CartViewDto {
  items: CartItemDto[];
  totalKrw: number;
  itemCount: number;
  unavailableCount: number;
  soldOutCount: number;
}

// 단위 + 수량 표시 문자열은 단위 메타(라벨/기준중량)가 필요하므로 units.ts 의 formatCartLineLabel(동적)로 이동했다.
// 호출부는 단위 목록을 함께 넘겨 표시 문자열을 만든다.
